import { useEffect, useId, useRef, useState } from "react";
import { releasesApi, type ReleasePr } from "@/api/releases";
export function PrTaskLink({
  pr,
  refresh,
  repair = false,
}: {
  repair?: boolean;
  pr: ReleasePr;
  refresh: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false),
    [search, setSearch] = useState(""),
    [selected, setSelected] = useState("");
  const [tasks, setTasks] = useState<{ id: string; title: string }[]>([]),
    [suggestions, setSuggestions] = useState<
      { id: string; title: string; reason: string }[]
    >([]);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [suggestionError, setSuggestionError] = useState("");
  const dialog = useRef<HTMLDialogElement>(null),
    lock = useRef(false),
    title = useId();
  useEffect(() => {
    if (open) dialog.current?.showModal();
    return () => dialog.current?.close();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    let active = true;
    void releasesApi
      .suggestions(pr)
      .then((data) => {
        if (active) {
          setSuggestions(data.tasks);
          setSuggestionError("");
        }
      })
      .catch(() => {
        if (active) setSuggestionError("לא ניתן לטעון הצעות; אפשר לחפש משימה.");
      });
    return () => {
      active = false;
    };
  }, [open, pr.repo, pr.number, pr.head_sha]);
  useEffect(() => {
    if (!open) return;
    let active = true;
    const timer = setTimeout(() => {
      void releasesApi
        .centerSearch(search, true)
        .then((data) => {
          if (active) {setTasks(data.tasks);setError(data.warning ?? '');}
        })
        .catch(() => {
          if (active) setError("חיפוש המשימות נכשל");
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [open, search]);
  async function run(create = false) {
    if (lock.current || releasesApi.workflowReadOnly) return;
    if (
      create &&
      !window.confirm(`ליצור משימה חדשה מה־PR #${pr.number} ולקשר אותה?`)
    )
      return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      await (create
        ? releasesApi.createTask(pr.repo, pr.number)
        : releasesApi.linkTask(pr.repo, pr.number, selected));
      await refresh();
      setOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <section className="rc-task-link" aria-label="משימת פיתוח">
      {pr.task ? (
        <a
          className="rc-task-chip"
          href={pr.task.url}
          target="_blank"
          rel="noreferrer"
        >
          משימה: {pr.task.title}
          {pr.task_link_state === "pending" && " · הקישור ממתין לאימות"}
          {pr.task_link_state === "unknown" && " · אימות הקישור אינו זמין"}
        </a>
      ) : (
        <>
          <p className="rc-reason">{pr.task_link_state === "unknown" ? "לא ניתן לאמת את קישור המשימה כרגע" : "לא מקושר למשימה · לא ייצא בלי קישור"}</p>
          <button onClick={() => setOpen(true)} className="min-h-11">קשר למשימה</button>
        </>
      )}
      {pr.task&&pr.database_task_id&&!pr.body_task_id&&<p>המשימה מקושרת במסד. חסרה שורת Task תקינה בגוף ה־PR; יש להשלימה במסירת הסוכן.</p>}
      {pr.task_link_conflict&&<p role="status">הקישור במסד הוא הקובע. מסד: <bdi>{pr.database_task_id}</bdi> · גוף ה־PR: <bdi>{pr.body_task_id}</bdi></p>}
      {pr.task && repair && <button className="min-h-11" onClick={()=>setOpen(true)}>קשר למשימה</button>}
      {open && (
        <dialog
          ref={dialog}
          aria-labelledby={title}
          dir="rtl"
          className="rc-link-dialog"
          onCancel={(e) => {
            if (busy) e.preventDefault();
            else setOpen(false);
          }}
        >
          <div className="rc-row">
            <h2 id={title}>לקשר את PR #{pr.number} למשימה</h2>
            <button
              disabled={busy}
              onClick={() => setOpen(false)}
              aria-label="סגירה"
            >
              ×
            </button>
          </div>
          <p>{pr.title}</p>
          <h3>הצעות לפי הענף, הכותרת והקבצים</h3>
          {suggestionError && <p role="status">{suggestionError}</p>}
          {suggestions.map((task) => (
            <button
              className="rc-suggestion"
              key={task.id}
              aria-pressed={selected === task.id}
              onClick={() => setSelected(task.id)}
            >
              <strong>{task.title}</strong>
              <small>{task.reason}</small>
            </button>
          ))}
          <label>
            או לחפש משימה
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelected("");
              }}
              placeholder="שם משימה, לקוח או מספר קריאה"
            />
          </label>
          <select
            aria-label="בחר משימה"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">בחר משימה</option>
            {[
              ...new Map(
                [...suggestions, ...tasks].map((t) => [t.id, t]),
              ).values(),
            ].map((task) => (
              <option key={task.id} value={task.id}>
                {task.title}
              </option>
            ))}
          </select>
          <button
            disabled={busy || !selected || releasesApi.workflowReadOnly}
            onClick={() => void run()}
          >
            לקשר למשימה שנבחרה
          </button>
          <button
            disabled={busy || releasesApi.workflowReadOnly}
            onClick={() => void run(true)}
          >
            לא מתאים — ליצור משימה חדשה מה־PR
          </button>
          <p>
            הקישור נשמר במסד דרך המסלול הקיים ובדיקת task-link רצה מחדש. הקישור במסד הוא הקובע.
          </p>
          {releasesApi.workflowReadOnly && (
            <p>תצוגה לקריאה בלבד — הקישור אינו נשלח מכאן.</p>
          )}
          {error && <p role="alert">{error}</p>}
        </dialog>
      )}
    </section>
  );
}
